// TODO: Delete this hook once foundations-components/components/Text exports a
// version that supports lineClamp truncation detection (scrollHeight >
// clientHeight). The upstream hook only checks scrollWidth > clientWidth, which
// misses vertical overflow from -webkit-line-clamp.
// Context: https://hubspot.slack.com/archives/C02HJPMPB9S/p1780064484168129
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export const useObserveTruncation = (content: ReactNode) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const checkTruncation = () => {
      const element = textRef.current;
      if (!element) return;

      const style = window.getComputedStyle(element);
      const lineClampValue = style.getPropertyValue('-webkit-line-clamp');
      const hasLineClamp = lineClampValue !== '' && lineClampValue !== 'none';
      const hasSingleLineTruncation =
        style.overflow === 'hidden' &&
        style.textOverflow === 'ellipsis' &&
        style.whiteSpace === 'nowrap';

      let isTextTruncated = false;
      if (hasLineClamp) {
        isTextTruncated = element.scrollHeight > element.clientHeight;
      } else if (hasSingleLineTruncation) {
        isTextTruncated = element.scrollWidth > element.clientWidth;
      }

      setIsTruncated(isTextTruncated);
    };

    checkTruncation();

    if (!observerRef.current) {
      observerRef.current = new ResizeObserver(() => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        animationFrameRef.current = requestAnimationFrame(checkTruncation);
      });
    }

    const element = textRef.current;
    const observer = observerRef.current;
    if (element && observer) {
      observer.observe(element);
    }

    return () => {
      if (element && observer) {
        observer.unobserve(element);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [content]);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  return { isTruncated, textRef };
};
